import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toPostUI } from '../../../mappers/posts/toPostUI';
import { DashboardPage } from '../../../ui/dashboard/DashboardPage';
import { IconProduct } from '../../../ui/icon/IconProduct';
import { Button } from '../../../ui/input/Button';
import { Box } from '../../../ui/layout/Box';
import { Flex } from '../../../ui/layout/Flex';
import { PostBusinessInfo } from '../../../ui/posts/PostBusinessInfo/PostBusinessInfo';
import { PostProductGrid } from '../../../ui/posts/PostProductGrid/PostProductGrid';
import { PostProductsFixedContainer } from '../../../ui/posts/PostProductsFixedContainer/PostProductsFixedContainer';
import { PostProductsNotFound } from '../../../ui/posts/PostProductsNotFound/PostProductsNotFound';
import { PostSingleProduct } from '../../../ui/posts/PostSingleProduct/PostSingleProduct';
import { Typography } from '../../../ui/typography/Typography';
import { analyticsService, EventSource } from '../../../utils/analyticsService';
import { useTranslation } from '../../../utils/useTranslation';
import { useSearchAppMetricsAnalytics } from '../../hooks/useSearchAppMetricsAnalytics';
import { usePostModel } from '../usePostModel';
import { addHideTabbarParam } from '../utils/addHideTabbarParam';
import { TrackProductClickOutProps } from './types';

/** Страница с товарами из поста для поискового приложения */
export const PostProductsDetails = () => {
    const params = useParams();
    const postId = params.postId!;
    const post = usePostModel({ postId });
    const postUI = useMemo(() => (post ? toPostUI(post) : undefined), [post]);
    const { products, business } = postUI || {};
    const postProductNotFound =
        !post || !postUI || post.status === 'deleted' || !products?.items?.length;

    const isMultipleProducts = products?.items && products.items.length > 1;

    const [hasShownLogged, setHasShownLogged] = useState(false);

    const t = useTranslation();

    const {
        trackPageShown,
        trackProductClick,
        trackButtonClick,
        trackProductShown,
        trackButtonShown,
    } = useSearchAppMetricsAnalytics({ post, source: 'post_products_page' });

    const trackProductClickOut = useCallback(
        (props: TrackProductClickOutProps) => {
            analyticsService.trackClickOut({
                postId,
                eventSource: EventSource.SEARCH_APP_FULLSCREEN,
                clickoutUrl: props.url,
                djRequestId: post?.djRequestId,
                logContext: post?.logContext,
                isBoosted: Boolean(post?.adsData),
                productId: props.productId,
                target: props.target,
                businessId: business?.id,
            });
        },
        [post, postId, business],
    );

    const onCatalogClick = useCallback(() => {
        if (business) {
            analyticsService.trackOpenCatalog({
                businessId: business.id,
                postId,
                eventSource: EventSource.SEARCH_APP_FULLSCREEN,
                djRequestId: post?.djRequestId,
                logContext: post?.logContext,
            });
        }
        business?.catalogUrl && trackButtonClick('Catalog', business.catalogUrl);
    }, [post, business, postId, trackButtonClick]);

    const onProductClick = useCallback(
        (productId: string, url: string) => {
            trackProductClickOut({
                url,
                target: 'product',
                productId,
            });
            trackProductClick(productId, url);
        },
        [trackProductClickOut, trackProductClick],
    );

    const onBusinessClick = useCallback(
        (url: string) => {
            trackProductClickOut({
                url,
                target: 'more_products',
            });
            trackButtonClick('GoToSite', url);
        },
        [trackButtonClick, trackProductClickOut],
    );

    const onBuyOnSiteClick = useCallback(
        (productId: string, url: string) => {
            trackProductClickOut({
                url,
                target: 'product',
                productId,
            });
            trackButtonClick('BuyOnSite', url);
        },
        [trackButtonClick, trackProductClickOut],
    );

    useEffect(() => {
        if (!hasShownLogged && business && !postProductNotFound) {
            trackPageShown();

            if (isMultipleProducts) {
                business?.catalogUrl && trackButtonShown('Catalog', business?.catalogUrl);
            }

            setHasShownLogged(true);
        }
    }, [
        isMultipleProducts,
        business,
        postProductNotFound,
        hasShownLogged,
        trackButtonShown,
        trackPageShown,
    ]);

    // аналог скелетона
    if (!post || !postUI) {
        return <DashboardPage hideHeader />;
    }

    if (postProductNotFound) {
        return (
            <DashboardPage hideHeader>
                <PostProductsNotFound />
            </DashboardPage>
        );
    }

    return (
        <DashboardPage
            hideHeader
            minHeight={{ xs: '100dvh', sm: '100vh' }}
            height={isMultipleProducts ? undefined : { xs: '100dvh', sm: '100vh' }}
            backgroundColor="var(--background-primary)"
        >
            <Flex direction="column" width="100%" height="100%">
                {isMultipleProducts ? (
                    <>
                        <Box
                            p={2}
                            position={'sticky'}
                            top={0}
                            width={'100%'}
                            zIndex={1}
                            background="var(--background-primary)"
                        >
                            <Typography variant="title3" weight="bold">
                                {t(products.title)}
                            </Typography>
                        </Box>

                        <PostProductGrid
                            products={products.items}
                            business={business}
                            onProductClick={onProductClick}
                            trackProductShown={trackProductShown}
                        />

                        <PostBusinessInfo
                            px={2}
                            business={business}
                            businessButtonTitle={products.businessButtonTitle}
                            onBusinessClick={onBusinessClick}
                            trackButtonShown={trackButtonShown}
                        />

                        <PostProductsFixedContainer mt="auto" p={2}>
                            <Flex width="100%" spacing={1}>
                                {business?.catalogUrl ? (
                                    <Button
                                        size="l"
                                        as={Link}
                                        to={addHideTabbarParam(business.catalogUrl)}
                                        width="100%"
                                        color="secondary"
                                        spacing={0.5}
                                        onClick={onCatalogClick}
                                    >
                                        <IconProduct fontSize={20} />
                                        {t(business.catalogTitle)}
                                    </Button>
                                ) : null}
                            </Flex>
                        </PostProductsFixedContainer>
                    </>
                ) : (
                    <>
                        <PostSingleProduct
                            product={products.items[0]}
                            business={business}
                            onBuyOnSiteClick={onBuyOnSiteClick}
                            onCatalogClick={onCatalogClick}
                            hideTabbar
                            businessButtonTitle={products.businessButtonTitle}
                            onBusinessClick={onBusinessClick}
                            trackProductShown={trackProductShown}
                            trackButtonShown={trackButtonShown}
                        />
                    </>
                )}
            </Flex>
        </DashboardPage>
    );
};
